import React, { useState, useEffect } from 'react';
import { X, FileSpreadsheet, Facebook, Table, Upload, AlertCircle } from 'lucide-react';
import { contactApi } from '../lib/api';
import type { Gender } from '../types/auth';
import Airtable from 'airtable';
import * as XLSX from 'xlsx';

interface ImportContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  city: string;
}

interface Contact {
  name: string;
  email: string;
  phone: string;
  gender: Gender;
  birthdate: string;
  city: string;
}

const ImportContactsModal: React.FC<ImportContactsModalProps> = ({ 
  isOpen, 
  onClose, 
  onImportComplete,
  city 
}) => {
  const [selectedSource, setSelectedSource] = useState<'excel' | 'meta' | 'airtable' | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Contact[]>([]);
  const [step, setStep] = useState<'source' | 'upload' | 'preview' | 'mapping'>('source');
  const [airtableConfig, setAirtableConfig] = useState({
    apiKey: '',
    baseId: '',
    tableName: ''
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFile(file);
    setError(null);
    setLoading(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      // Validate and transform the data
      const contacts: Contact[] = jsonData.map((row: any) => {
        // Normalize field names (case-insensitive)
        const normalizedRow = Object.keys(row).reduce((acc: any, key) => {
          acc[key.toLowerCase()] = row[key];
          return acc;
        }, {});

        // Extract data with flexible field names
        const name = (normalizedRow.name || normalizedRow.nome || '').toString().toUpperCase();
        const email = (normalizedRow.email || normalizedRow.mail || normalizedRow['e-mail'] || '').toString();
        const phone = (normalizedRow.phone || normalizedRow.telefono || normalizedRow.cellulare || '').toString();
        const gender = (normalizedRow.gender || normalizedRow.sesso || normalizedRow.genere || 'non_dichiarato').toString().toLowerCase();
        let birthdate = normalizedRow.birthdate || normalizedRow['data di nascita'] || normalizedRow.nascita || '';

        const genderMap: Record<string, Gender> = {
          'uomo': 'uomo_etero',
          'uomo_etero': 'uomo_etero',
          'uomo etero': 'uomo_etero',
          'donna': 'donna_etero',
          'donna_etero': 'donna_etero',
          'donna etero': 'donna_etero',
          'uomo_gay': 'uomo_gay',
          'uomo gay': 'uomo_gay',
          'donna_gay': 'donna_gay',
          'donna gay': 'donna_gay',
          'non_dichiarato': 'non_dichiarato',
          'non dichiarato': 'non_dichiarato',
        };
        const validGender: Gender = genderMap[gender] || 'non_dichiarato';

        // Format birthdate if needed
        let formattedBirthdate = birthdate;
        if (birthdate instanceof Date) {
          formattedBirthdate = birthdate.toISOString().split('T')[0];
        } else if (typeof birthdate === 'number') {
          // Handle Excel date number
          formattedBirthdate = new Date((birthdate - 25569) * 86400 * 1000).toISOString().split('T')[0];
        } else if (typeof birthdate === 'string') {
          // Try to parse various date formats
          const dateParts = birthdate.split(/[\/\-\.]/);
          if (dateParts.length === 3) {
            // Assume DD/MM/YYYY format if day is <= 31
            const [first, second, year] = dateParts;
            if (parseInt(first) <= 31) {
              formattedBirthdate = `${year}-${second.padStart(2, '0')}-${first.padStart(2, '0')}`;
            } else {
              formattedBirthdate = `${year}-${first.padStart(2, '0')}-${second.padStart(2, '0')}`;
            }
          }
        }

        // Generate a default email if not provided
        const defaultEmail = `${name.toLowerCase().replace(/[^a-z0-9]/g, '.')}@noemail.com`;

        return {
          name,
          email: email || defaultEmail, // Use default email if not provided
          phone,
          gender: validGender,
          birthdate: formattedBirthdate,
          city
        };
      }).filter(contact => 
        contact.name && // Name is required
        contact.phone && // Phone is required
        contact.birthdate // Birthdate is required
      );

      if (contacts.length === 0) {
        throw new Error('Nessun contatto valido trovato nel file');
      }

      setPreview(contacts);
      setStep('preview');
    } catch (err: any) {
      console.error('Error reading Excel file:', err);
      setError(err.message || 'Errore nella lettura del file Excel');
    } finally {
      setLoading(false);
    }
  };

  const handleMetaAdsConnect = () => {
    setError('La funzionalità di importazione da Meta Ads sarà disponibile a breve.');
  };

  const handleAirtableConnect = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!airtableConfig.apiKey || !airtableConfig.baseId || !airtableConfig.tableName) {
        throw new Error('Inserisci tutti i dati di configurazione di Airtable');
      }

      // Configura Airtable
      Airtable.configure({
        apiKey: airtableConfig.apiKey
      });

      const base = Airtable.base(airtableConfig.baseId);

      // Recupera i record dalla tabella specificata
      const records = await base(airtableConfig.tableName).select({
        maxRecords: 100,
        view: "Grid view"
      }).all();

      // Mappa i record nel formato richiesto
      const contacts: Contact[] = records.map(record => {
        const name = (record.get('Name') as string || '').toUpperCase();
        const email = record.get('Email') as string || '';
        const defaultEmail = `${name.toLowerCase().replace(/[^a-z0-9]/g, '.')}@noemail.com`;

        return {
          name,
          email: email || defaultEmail, // Use default email if not provided
          phone: record.get('Phone') as string || '',
          gender: ({'uomo': 'uomo_etero', 'donna': 'donna_etero', 'uomo_etero': 'uomo_etero', 'donna_etero': 'donna_etero', 'uomo_gay': 'uomo_gay', 'donna_gay': 'donna_gay', 'non_dichiarato': 'non_dichiarato'}[(record.get('Gender') as string || 'non_dichiarato').toLowerCase()] || 'non_dichiarato') as Gender,
          birthdate: record.get('Birthdate') as string || '',
          city
        };
      }).filter(contact => 
        contact.name && 
        contact.phone && 
        contact.birthdate
      );

      if (contacts.length === 0) {
        throw new Error('Nessun contatto valido trovato nella tabella Airtable');
      }

      setPreview(contacts);
      setStep('preview');
    } catch (err: any) {
      console.error('Error connecting to Airtable:', err);
      setError(err.message || 'Errore durante la connessione ad Airtable');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    try {
      setLoading(true);
      setError(null);

      // Importa i contatti in batch
      for (const contact of preview) {
        await contactApi.create(contact);
      }

      onImportComplete();
      onClose();
    } catch (error: any) {
      console.error('Error importing contacts:', error);
      setError(error.message || 'Errore durante l\'importazione dei contatti');
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setSelectedSource(null);
    setFile(null);
    setError(null);
    setPreview([]);
    setStep('source');
    setAirtableConfig({
      apiKey: '',
      baseId: '',
      tableName: ''
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Importa Contatti</h2>
          <button 
            onClick={() => {
              onClose();
              resetModal();
            }} 
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            <p>{error}</p>
          </div>
        )}

        {step === 'source' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => {
                setSelectedSource('excel');
                setStep('upload');
              }}
              className="p-6 border border-gray-200 rounded-lg hover:border-rose-500 hover:bg-rose-50 transition-colors text-center"
            >
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-rose-500" />
              <h3 className="font-medium mb-2">Excel/CSV</h3>
              <p className="text-sm text-gray-500">
                Importa da un file Excel o CSV
              </p>
            </button>

            <button
              onClick={handleMetaAdsConnect}
              className="p-6 border border-gray-200 rounded-lg hover:border-rose-500 hover:bg-rose-50 transition-colors text-center"
            >
              <Facebook className="h-12 w-12 mx-auto mb-4 text-rose-500" />
              <h3 className="font-medium mb-2">Meta Ads</h3>
              <p className="text-sm text-gray-500">
                Importa da Meta Ads
              </p>
            </button>

            <button
              onClick={() => {
                setSelectedSource('airtable');
                setStep('upload');
              }}
              className="p-6 border border-gray-200 rounded-lg hover:border-rose-500 hover:bg-rose-50 transition-colors text-center"
            >
              <Table className="h-12 w-12 mx-auto mb-4 text-rose-500" />
              <h3 className="font-medium mb-2">Airtable</h3>
              <p className="text-sm text-gray-500">
                Importa da Airtable
              </p>
            </button>
          </div>
        )}

        {step === 'upload' && selectedSource === 'excel' && (
          <div className="text-center">
            <div className="mb-8">
              <Upload className="h-12 w-12 mx-auto mb-4 text-rose-500" />
              <h3 className="font-medium mb-2">Carica File Excel/CSV</h3>
              <p className="text-sm text-gray-500">
                Seleziona un file Excel (.xlsx) o CSV (.csv) dal tuo computer
              </p>
            </div>

            <label className="inline-flex items-center px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 cursor-pointer transition-colors">
              <Upload className="h-4 w-4 mr-2" />
              Seleziona File
              <input
                type="file"
                accept=".xlsx,.csv"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>

            <button
              onClick={() => setStep('source')}
              className="ml-4 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Indietro
            </button>
          </div>
        )}

        {step === 'upload' && selectedSource === 'airtable' && (
          <div>
            <div className="mb-6">
              <h3 className="font-medium mb-4">Configura Airtable</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    API Key
                  </label>
                  <input
                    type="text"
                    value={airtableConfig.apiKey}
                    onChange={(e) => setAirtableConfig(prev => ({ ...prev, apiKey: e.target.value }))}
                    placeholder="Inserisci la tua API Key di Airtable"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Puoi trovare la tua API Key nelle impostazioni del tuo account Airtable
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Base ID
                  </label>
                  <input
                    type="text"
                    value={airtableConfig.baseId}
                    onChange={(e) => setAirtableConfig(prev => ({ ...prev, baseId: e.target.value }))}
                    placeholder="Inserisci l'ID della base Airtable"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Puoi trovare il Base ID nell'URL della tua base Airtable
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Tabella
                  </label>
                  <input
                    type="text"
                    value={airtableConfig.tableName}
                    onChange={(e) => setAirtableConfig(prev => ({ ...prev, tableName: e.target.value }))}
                    placeholder="Inserisci il nome della tabella"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setStep('source')}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Indietro
              </button>
              <button
                onClick={handleAirtableConnect}
                disabled={loading}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Table className="h-4 w-4" />
                {loading ? 'Connessione...' : 'Connetti'}
              </button>
            </div>
          </div>
        )}

        {step === 'preview' && preview.length > 0 && (
          <div>
            <h3 className="font-medium mb-4">Anteprima Contatti</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Nome</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Telefono</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Genere</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Data di Nascita</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((contact, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-3 px-4">{contact.name}</td>
                      <td className="py-3 px-4">
                        {contact.email.endsWith('@noemail.com') ? (
                          <span className="text-gray-400 italic">Email non fornita</span>
                        ) : contact.email}
                      </td>
                      <td className="py-3 px-4">{contact.phone}</td>
                      <td className="py-3 px-4 capitalize">{contact.gender}</td>
                      <td className="py-3 px-4">
                        {new Date(contact.birthdate).toLocaleDateString('it-IT')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setStep('upload');
                  setPreview([]);
                  setFile(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Indietro
              </button>
              <button
                onClick={handleImport}
                disabled={loading}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Upload className="h-4 w-4" />
                {loading ? 'Importazione...' : 'Importa Contatti'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportContactsModal;